import React, {useRef, useState, useEffect, useLayoutEffect, useCallback} from 'react';
import {createPortal} from 'react-dom';
import classnames from 'classnames';
import styles from '../../styles/components/LocationFlexible.module.scss';

// Gap between the (i) icon and the tooltip box, and the minimum distance the
// box keeps from the viewport edges (in px).
const OFFSET = 8;
const VIEWPORT_MARGIN = 8;
// Keep the arrow away from the box's rounded corners.
const ARROW_INSET = 12;

// (i) icon with a tooltip for table cells. The tooltip is rendered into
// document.body with `position: fixed`, so it isn't clipped by the table's
// `overflow-x: auto` wrapper, and its horizontal position is clamped to the
// viewport so it never gets cut off at the left or right edge.
export default function TableTooltip({text}) {
    const triggerRef = useRef(null);
    const popupRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [position, setPosition] = useState(null);

    const updatePosition = useCallback(() => {
        const trigger = triggerRef.current;
        const popup = popupRef.current;
        if (!trigger || !popup) {
            return;
        }

        const triggerRect = trigger.getBoundingClientRect();
        const {width, height} = popup.getBoundingClientRect();
        const viewportWidth = document.documentElement.clientWidth;
        const iconCenter = triggerRect.left + triggerRect.width / 2;

        const maxLeft = Math.max(VIEWPORT_MARGIN, viewportWidth - VIEWPORT_MARGIN - width);
        const left = Math.min(Math.max(iconCenter - width / 2, VIEWPORT_MARGIN), maxLeft);

        // Prefer showing the tooltip above the icon; flip below if there is no room.
        let top = triggerRect.top - height - OFFSET;
        const placement = top < VIEWPORT_MARGIN ? 'bottom' : 'top';
        if (placement === 'bottom') {
            top = triggerRect.bottom + OFFSET;
        }

        // Keep the arrow pointing at the icon even when the box was shifted.
        const arrowLeft = Math.min(Math.max(iconCenter - left, ARROW_INSET), width - ARROW_INSET);

        setPosition({top, left, arrowLeft, placement});
    }, []);

    // Measure before paint so the tooltip never flashes at the wrong position.
    useLayoutEffect(() => {
        if (open) {
            updatePosition();
        } else {
            setPosition(null);
        }
    }, [open, updatePosition]);

    // Follow the icon while the page or the table (horizontal scroll) scrolls.
    useEffect(() => {
        if (!open) {
            return undefined;
        }
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                setOpen(false);
            }
        };
        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [open, updatePosition]);

    const show = () => setOpen(true);
    const hide = () => setOpen(false);

    return (
        <span
            ref={triggerRef}
            className={styles.tooltip}
            tabIndex={0}
            aria-label={text}
            onMouseEnter={show}
            onMouseLeave={hide}
            onFocus={show}
            onBlur={hide}
        >
            <span className={styles.tooltipIcon} aria-hidden="true">i</span>
            {open && createPortal(
                <span
                    ref={popupRef}
                    role="tooltip"
                    className={classnames(styles.tooltipText, {
                        [styles.tooltipTextVisible]: position,
                        [styles.tooltipTextBelow]: position?.placement === 'bottom',
                    })}
                    style={position ? {top: position.top, left: position.left} : undefined}
                >
                    {text}
                    <span
                        className={styles.tooltipArrow}
                        style={position ? {left: position.arrowLeft} : undefined}
                    />
                </span>,
                document.body
            )}
        </span>
    );
}
